import { LatestTradeType, TokenType } from "types/token.type";
import { getSupplyScalingFactor } from "utils/curve";

export function truncate(
  value?: string,
  length = 13,
  position: "start" | "end" | "middle" = "middle",
  addDots = true
) {
  if (!value) {
    return value;
  }
  if (value?.length <= length) {
    return value;
  }

  const separator = addDots ? "..." : "";
  const stringLength = length - separator.length;
  const frontLength = Math.ceil(stringLength / 2);
  const backLength = Math.floor(stringLength / 2);

  return position === "end"
    ? value.substring(0, length) + separator
    : value.substring(0, frontLength) +
    separator +
    value.substring(value.length - backLength);
}

export function numberWithCommas(input: string) {
  return input.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatDate(timestamp: string) {
  const date: Date = new Date(timestamp);
  const formattedDate: string = date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return formattedDate;
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}


export const formatTime = (timestamp: Date) => {
  const now = new Date().getTime();
  const time = new Date(timestamp).getTime();
  const diff = now - time;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  if (minutes < 60) {
    return `${minutes}min ago`;
  }

  if (hours < 24) {
    return `${hours}h ago`;
  }

  if (days < 30) {
    return `${days}D ago`;
  }

  if (months < 12) {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(',', '');
  }

  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(',', '');
};

export const convertToStandardIPFS = (url: string): string => {
  if (!url) return url;
  
  const pinataPattern = /https:\/\/[^\/]+\.mypinata\.cloud\/ipfs\/([a-zA-Z0-9]+)/;
  const match = url.match(pinataPattern);
  
  if (match) {
    const ipfsHash = match[1];
    return `https://ipfs.io/ipfs/${ipfsHash}`;
  }
  
  return url;
};

export const clamp = (value: number, min = 0, max = 1): number => {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

const toNumeric = (value: unknown, fallback = 0): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return fallback;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  if (value instanceof Date) {
    const parsed = value.getTime();
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  if (typeof value === "bigint") {
    return Number(value);
  }
  return fallback;
};

const computeUsdValue = (valueEth: number, ethPriceUsd: number): number => {
  if (!Number.isFinite(valueEth) || !Number.isFinite(ethPriceUsd)) {
    return 0;
  }
  return valueEth * ethPriceUsd;
};

const ensureTimestamp = (value?: string): string => {
  if (!value) {
    return new Date().toISOString();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString();
  }
  return date.toISOString();
};

type TradeLike = Partial<LatestTradeType>;

export const normalizeLatestTrade = (
  trade: TradeLike,
  overrides: { ethPriceUsd?: number } = {}
): LatestTradeType => {
  const ethPriceUsd = toNumeric(overrides.ethPriceUsd ?? trade.ethPriceUsd);

  const ethAmount = toNumeric(trade.ethAmount ?? trade.lamportAmount);
  const tokenAmount = toNumeric(trade.tokenAmount);

  const impliedEthPrice = Number.isFinite(trade.impliedEthPrice as number)
    ? toNumeric(trade.impliedEthPrice)
    : tokenAmount > 0
      ? ethAmount / tokenAmount
      : 0;

  const computedImpliedUsd =
    impliedEthPrice > 0 && ethPriceUsd > 0 ? impliedEthPrice * ethPriceUsd : NaN;
  const impliedUsdPrice = Number.isFinite(computedImpliedUsd)
    ? computedImpliedUsd
    : toNumeric(trade.impliedUsdPrice);

  const marketCapEth = toNumeric(trade.marketCapEth);
  const marketCapUsd =
    ethPriceUsd > 0
      ? computeUsdValue(marketCapEth, ethPriceUsd)
      : toNumeric(trade.marketCapUsd);

  const normalizedMarketCapEth = toNumeric(trade.normalizedMarketCapEth);
  const normalizedMarketCapUsd =
    ethPriceUsd > 0
      ? computeUsdValue(normalizedMarketCapEth, ethPriceUsd)
      : toNumeric(trade.normalizedMarketCapUsd);

  const targetMarketCapEth = toNumeric(trade.targetMarketCapEth);
  const targetMarketCapUsd =
    ethPriceUsd > 0
      ? computeUsdValue(targetMarketCapEth, ethPriceUsd)
      : toNumeric(trade.targetMarketCapUsd);

  const progressMcap = toNumeric(
    trade.progressMcap ?? marketCapEth ?? normalizedMarketCapEth
  );

  const amount = toNumeric(
    trade.amount ?? trade.ethAmount ?? trade.lamportAmount
  );

  return {
    token: trade.token ?? "",
    name: trade.name ?? "",
    ticker: trade.ticker ?? "",
    creator: trade.creator ?? trade.creatorAddress ?? "",
    creatorDisplayName: trade.creatorDisplayName ?? trade.creator ?? "",
    avatar: trade.avatar ?? trade.url ?? "",
    url: trade.url ?? "",
    progressMcap,
    marketCapEth,
    marketCapUsd,
    normalizedMarketCapEth,
    normalizedMarketCapUsd,
    reply: toNumeric(trade.reply),
    action: (trade.action ?? trade.swapDirection ?? "").toString().toUpperCase(),
    amount,
    ethAmount,
    lamportAmount: toNumeric(trade.lamportAmount ?? trade.ethAmount ?? ethAmount),
    tokenAmount,
    price: toNumeric(trade.price ?? impliedUsdPrice),
    impliedEthPrice,
    impliedUsdPrice,
    ethPriceUsd,
    ethRaised: toNumeric(trade.ethRaised),
    targetRaiseEth: toNumeric(trade.targetRaiseEth),
    targetMarketCapEth,
    targetMarketCapUsd,
    timestamp: ensureTimestamp(trade.timestamp ?? trade.time),
    tx: trade.tx ?? "",
  };
};

export const normalizeToken = (
  token: TokenType,
  ethPriceUsd: number
): TokenType => {
  const effectiveEthPriceUsd =
    Number.isFinite(ethPriceUsd) && ethPriceUsd > 0
      ? ethPriceUsd
      : toNumeric(token.ethPriceUsd);

  const impliedEthPrice = toNumeric(token.impliedEthPrice);
  const computedImpliedUsd =
    impliedEthPrice > 0 && effectiveEthPriceUsd > 0
      ? impliedEthPrice * effectiveEthPriceUsd
      : NaN;
  const impliedUsdPrice = Number.isFinite(computedImpliedUsd)
    ? computedImpliedUsd
    : toNumeric(token.impliedUsdPrice);

  const marketCapEth = toNumeric(token.marketCapEth ?? token.progressMcap);
  const marketCapUsd =
    effectiveEthPriceUsd > 0
      ? computeUsdValue(marketCapEth, effectiveEthPriceUsd)
      : toNumeric(token.marketCapUsd);

  const normalizedMarketCapEth = toNumeric(token.normalizedMarketCapEth);
  const normalizedMarketCapUsd =
    effectiveEthPriceUsd > 0
      ? computeUsdValue(normalizedMarketCapEth, effectiveEthPriceUsd)
      : toNumeric(token.normalizedMarketCapUsd);

  const targetMarketCapEth = toNumeric(token.targetMarketCapEth);
  const targetMarketCapUsd =
    effectiveEthPriceUsd > 0
      ? computeUsdValue(targetMarketCapEth, effectiveEthPriceUsd)
      : toNumeric(token.targetMarketCapUsd);

  return {
    ...token,
    ethPriceUsd: effectiveEthPriceUsd,
    impliedEthPrice,
    impliedUsdPrice,
    marketCapEth,
    marketCapUsd,
    normalizedMarketCapEth,
    normalizedMarketCapUsd,
    targetMarketCapEth,
    targetMarketCapUsd,
  };
};

export interface TokenMarketSummary {
  token: TokenType;
  priceEth: number;
  priceUsd: number;
  normalizedMarketCapEth: number;
  normalizedMarketCapUsd: number;
  targetMarketCapEth: number;
  targetMarketCapUsd: number;
  progressPercent: number;
  ethPriceUsd: number;
}

export const getTokenMarketSummary = (
  token: TokenType,
  fallbackEthPriceUsd: number = 0
): TokenMarketSummary => {
  const baseEthPriceUsd =
    Number.isFinite(fallbackEthPriceUsd) && fallbackEthPriceUsd > 0
      ? fallbackEthPriceUsd
      : toNumeric(token.ethPriceUsd);

  const normalized = normalizeToken(token, baseEthPriceUsd);

  const impliedPriceEth = normalized.impliedEthPrice ?? 0;
  const impliedPriceUsd =
    normalized.impliedUsdPrice ??
    (impliedPriceEth > 0 && baseEthPriceUsd > 0 ? impliedPriceEth * baseEthPriceUsd : 0);

  const scaling = getSupplyScalingFactor(normalized);
  const priceEth = impliedPriceEth * scaling;
  const priceUsd = impliedPriceUsd * scaling;

  const normalizedMarketCapEth =
    normalized.normalizedMarketCapEth ?? normalized.marketCapEth ?? 0;
  const normalizedMarketCapUsd =
    normalized.normalizedMarketCapUsd ?? normalized.marketCapUsd ?? 0;

  const targetMarketCapEth = normalized.targetMarketCapEth ?? 0;
  const targetMarketCapUsd =
    normalized.targetMarketCapUsd ??
    (targetMarketCapEth > 0 && baseEthPriceUsd > 0
      ? targetMarketCapEth * baseEthPriceUsd
      : 0);

  const ethRaised = toNumeric(normalized.ethRaised ?? token.ethRaised);
  const targetRaiseEth = toNumeric(
    normalized.targetRaiseEth ?? token.targetRaiseEth ?? 0
  );

  let progressPercent = 0;

  if (targetRaiseEth > 0) {
    progressPercent = clamp((ethRaised / targetRaiseEth) * 100, 0, 100);
  } else if (targetMarketCapUsd > 0) {
    progressPercent = clamp((normalizedMarketCapUsd / targetMarketCapUsd) * 100, 0, 100);
  }

  return {
    token: normalized,
    priceEth,
    priceUsd,
    normalizedMarketCapEth,
    normalizedMarketCapUsd,
    targetMarketCapEth,
    targetMarketCapUsd,
    progressPercent,
    ethPriceUsd: baseEthPriceUsd,
  };
};
